import type { Center } from '@api/types';
import { PlaceholderCard } from '@components/admin/PlaceholderCard';
import { DAY_NAMES } from './SettingsPage.helpers';
import type { DayState } from './SettingsPage.helpers';

interface GeneralSectionProps {
  center: Center | undefined;
  bhEnabled: boolean;
  setBhEnabled: (v: boolean) => void;
  bhDays: DayState[];
  setBhDays: React.Dispatch<React.SetStateAction<DayState[]>>;
}

export function GeneralSection({ center, bhEnabled, setBhEnabled, bhDays, setBhDays }: GeneralSectionProps) {
  return (
    <>
      {/* Datos fiscales (placeholder) */}
      <PlaceholderCard
        title="Datos del negocio"
        description="Información que se muestra en facturas y emails."
        preview={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="field-row">
              <div className="field">
                <label className="field-label">Razón social</label>
                <input className="input" disabled placeholder="Tu empresa S.L." />
              </div>
              <div className="field">
                <label className="field-label">CIF / NIF</label>
                <input className="input" disabled placeholder="B-12345678" />
              </div>
            </div>
            <div className="field">
              <label className="field-label">Dirección fiscal</label>
              <input className="input" disabled placeholder="Calle, número, ciudad, CP" />
            </div>
            <div className="field">
              <label className="field-label">Email de soporte</label>
              <input className="input" disabled placeholder="hola@tucentro.com" />
            </div>
          </div>
        }
      />

      {/* Marca per-centre (placeholder) */}
      <PlaceholderCard
        title="Marca"
        description="Cómo se ve el widget de reservas."
        preview={
          <div className="field-row">
            <div className="field">
              <label className="field-label">Color principal</label>
              <input className="input mono" disabled placeholder="#B23A2A" />
            </div>
            <div className="field">
              <label className="field-label">Idioma por defecto</label>
              <select className="select" disabled>
                <option>Español</option>
              </select>
            </div>
          </div>
        }
      />

      {/* Centro (read-only) — name + timezone + slug. Edita superadmin desde /admin/centers */}
      <div className="card">
        <div className="card-head">
          <span className="card-title">Centro</span>
        </div>
        <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ fontSize: 12.5, color: 'var(--fg-muted)', margin: 0 }}>
            Estos datos los gestiona el superadmin desde el panel de centros.
          </p>
          <div className="field-row">
            <div className="field">
              <label className="field-label">Nombre</label>
              <input className="input" value={center?.name ?? ''} readOnly style={{ color: 'var(--fg-muted)' }} />
            </div>
            <div className="field">
              <label className="field-label">Slug</label>
              <input className="input mono" value={center?.slug ?? ''} readOnly style={{ color: 'var(--fg-muted)' }} />
            </div>
          </div>
          <div className="field">
            <label className="field-label">Zona horaria</label>
            <input className="input" value={center?.timezone ?? ''} readOnly style={{ color: 'var(--fg-muted)' }} />
          </div>
        </div>
      </div>

      {/* Horario de apertura (real) */}
      <div className="card">
        <div className="card-head">
          <span className="card-title">Horario de apertura</span>
        </div>
        <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={bhEnabled}
              onChange={(e) => { setBhEnabled(e.target.checked); }}
              style={{ width: 14, height: 14, accentColor: 'var(--accent)' }}
            />
            <span style={{ fontSize: 13.5, fontWeight: 500 }}>Activar horario de apertura</span>
          </label>
          <p style={{ fontSize: 11.5, color: 'var(--fg-soft)', margin: 0 }}>
            Si está desactivado, el centro aparece disponible las 24 h. Si está activado, los clientes solo
            pueden reservar dentro del horario configurado.
          </p>
          {bhEnabled && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 4 }}>
              <tbody>
                {DAY_NAMES.map((dayName, i) => (
                  <tr key={dayName} style={{ borderTop: i === 0 ? 'none' : '1px solid var(--border)' }}>
                    <td style={{ padding: '5px 0', width: 90, color: 'var(--fg-soft)', fontWeight: 500 }}>{dayName}</td>
                    <td style={{ padding: '5px 0', width: 88 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 12 }}>
                        <input
                          type="checkbox"
                          checked={bhDays[i].closed}
                          onChange={(e) => {
                            setBhDays((prev) => prev.map((d, j) => j === i ? { ...d, closed: e.target.checked } : d));
                          }}
                          style={{ width: 13, height: 13, accentColor: 'var(--accent)' }}
                        />
                        Cerrado
                      </label>
                    </td>
                    <td style={{ padding: '5px 0' }}>
                      {!bhDays[i].closed && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input
                            type="time"
                            className="input mono"
                            value={bhDays[i].open}
                            onChange={(e) => {
                              setBhDays((prev) => prev.map((d, j) => j === i ? { ...d, open: e.target.value } : d));
                            }}
                            style={{ width: 90, padding: '3px 6px', fontSize: 12 }}
                          />
                          <span style={{ color: 'var(--fg-muted)' }}>—</span>
                          <input
                            type="time"
                            className="input mono"
                            value={bhDays[i].close}
                            onChange={(e) => {
                              setBhDays((prev) => prev.map((d, j) => j === i ? { ...d, close: e.target.value } : d));
                            }}
                            style={{ width: 90, padding: '3px 6px', fontSize: 12 }}
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
